import React, { useState } from 'react';
import { Form, Button, Container, Row, Col, Card } from 'react-bootstrap';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';

interface CapitalGainsCalculatorProps {
  currentUser: User | null;
}

const CapitalGainsCalculator: React.FC<CapitalGainsCalculatorProps> = ({ currentUser }) => {
  const [assetType, setAssetType] = useState('equity');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [result, setResult] = useState<any>(null);

  const ciiData: { [key: string]: number } = {
    '2001-02': 100, '2002-03': 105, '2003-04': 109, '2004-05': 113, '2005-06': 117,
    '2006-07': 122, '2007-08': 129, '2008-09': 137, '2009-10': 148, '2010-11': 167,
    '2011-12': 184, '2012-13': 200, '2013-14': 220, '2014-15': 240, '2015-16': 254,
    '2016-17': 264, '2017-18': 272, '2018-19': 280, '2019-20': 289, '2020-21': 301,
    '2021-22': 317, '2022-23': 331, '2023-24': 348, '2024-25': 363, '2025-26': 376,
  };

  const getFinancialYear = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return month >= 3 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
  };

  const calculateGains = () => {
    const pDate = new Date(purchaseDate);
    const sDate = new Date(saleDate);
    const pPrice = parseFloat(purchasePrice);
    const sPrice = parseFloat(salePrice);

    if (isNaN(pDate.getTime()) || isNaN(sDate.getTime()) || isNaN(pPrice) || isNaN(sPrice)) {
      setResult({ error: 'Please enter valid numbers and dates.' });
      return;
    }

    const holdingPeriod = (sDate.getTime() - pDate.getTime()) / (1000 * 3600 * 24 * 365.25);
    const capitalGain = sPrice - pPrice;
    let gainType = '';
    let indexedCost = pPrice;
    let taxRate = 0;
    let taxAmount = 0;
    let finalGain = capitalGain;

    const saleFinancialYear = getFinancialYear(sDate);
    const purchaseFinancialYear = getFinancialYear(pDate);

    const ciiPurchase = ciiData[purchaseFinancialYear] || 0;
    const ciiSale = ciiData[saleFinancialYear] || 0;

    if (assetType === 'equity') {
      gainType = holdingPeriod > 1 ? 'Long-term' : 'Short-term';
      if (gainType === 'Long-term') {
        const exemption = 125000;
        const taxableGain = Math.max(0, capitalGain - exemption);
        taxRate = sDate >= new Date('2024-07-23') ? 0.125 : 0.1;
        taxAmount = taxableGain * taxRate;
        finalGain = capitalGain;
      } else {
        taxRate = sDate >= new Date('2024-07-23') ? 0.20 : 0.15;
        taxAmount = capitalGain * taxRate;
      }
    } else if (assetType === 'property') {
      gainType = holdingPeriod > 2 ? 'Long-term' : 'Short-term';
      if (gainType === 'Long-term') {
        if (sDate >= new Date('2024-07-23')) {
          // User choice simulation - assuming they pick the lower tax
          const taxWithoutIndexation = capitalGain * 0.125;
          indexedCost = (pPrice * ciiSale) / ciiPurchase;
          const gainWithIndexation = sPrice - indexedCost;
          const taxWithIndexation = gainWithIndexation * 0.20;
          if (taxWithIndexation < taxWithoutIndexation) {
            finalGain = gainWithIndexation;
            taxAmount = taxWithIndexation;
            taxRate = 0.20;
          } else {
            taxAmount = taxWithoutIndexation;
            taxRate = 0.125;
          }
        } else {
            indexedCost = (pPrice * ciiSale) / ciiPurchase;
            finalGain = sPrice - indexedCost;
            taxRate = 0.20;
            taxAmount = finalGain * taxRate;
        }
      } else {
        // STCG on property is added to income and taxed at slab rates.
        // We'll just show the gain and a note.
        finalGain = capitalGain;
        taxRate = 0; // Represents slab rate
        taxAmount = 0;
      }
    } else if (assetType === 'debt') {
        gainType = holdingPeriod > 3 ? 'Long-term' : 'Short-term';
        if (gainType === 'Long-term') {
            if (sDate >= new Date('2024-07-23')) {
                taxRate = 0.125;
                taxAmount = capitalGain * taxRate;
            } else {
                indexedCost = (pPrice * ciiSale) / ciiPurchase;
                finalGain = sPrice - indexedCost;
                taxRate = 0.20;
                taxAmount = finalGain * taxRate;
            }
        } else {
            finalGain = capitalGain;
            taxRate = 0; // Represents slab rate
            taxAmount = 0;
        }
    }

    setResult({
      holdingPeriod: holdingPeriod.toFixed(2),
      capitalGain: capitalGain.toFixed(2),
      gainType,
      indexedCost: indexedCost.toFixed(2),
      finalGain: finalGain.toFixed(2),
      taxRate: taxRate * 100,
      taxAmount: taxAmount.toFixed(2),
    });
  };

  return (
    <Container className="mt-5">
      <Row>
        <Col md={{ span: 8, offset: 2 }}>
          <Card>
            <Card.Body>
              <h3 className="text-center mb-4">Capital Gains Calculator</h3>
              <Form>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={4}>Asset Type</Form.Label>
                  <Col sm={8}>
                    <Form.Control as="select" value={assetType} onChange={(e) => setAssetType(e.target.value)}>
                      <option value="equity">Listed Equity/Equity MF</option>
                      <option value="property">Real Estate</option>
                      <option value="debt">Debt Mutual Funds</option>
                    </Form.Control>
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={4}>Purchase Date</Form.Label>
                  <Col sm={8}>
                    <Form.Control type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={4}>Sale Date</Form.Label>
                  <Col sm={8}>
                    <Form.Control type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={4}>Purchase Price (₹)</Form.Label>
                  <Col sm={8}>
                    <Form.Control type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm={4}>Sale Price (₹)</Form.Label>
                  <Col sm={8}>
                    <Form.Control type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} />
                  </Col>
                </Form.Group>
                <div className="text-center">
                    <Button variant="primary" onClick={calculateGains}>Calculate</Button>
                    {currentUser && result && !result.error && (
                      <Button variant="secondary" onClick={async () => {
                        try {
                          await addDoc(collection(db, `users/${currentUser.uid}/savedCalculations`), {
                            calculatorType: 'capitalGains',
                            assetType,
                            purchaseDate,
                            saleDate,
                            purchasePrice,
                            salePrice,
                            result,
                            timestamp: new Date(),
                          });
                          alert('Capital Gains calculation saved!');
                        } catch (e) {
                          console.error('Error saving document: ', e);
                          alert('Error saving calculation.');
                        }
                      }} className="ms-2">
                        Save Calculation
                      </Button>
                    )}
                </div>
              </Form>
              {result && (
                <div className="mt-4">
                  <h4 className="text-center">Results</h4>
                  {result.error ? (
                    <p className="text-danger text-center">{result.error}</p>
                  ) : (
                    <div>
                      <p><strong>Holding Period:</strong> {result.holdingPeriod} years</p>
                      <p><strong>Type of Gain:</strong> {result.gainType}</p>
                      <p><strong>Capital Gain:</strong> ₹{result.capitalGain}</p>
                      {result.gainType === 'Long-term' && assetType !== 'equity' && (
                        <p><strong>Indexed Cost of Acquisition:</strong> ₹{result.indexedCost}</p>
                      )}
                      <p><strong>Final (Taxable) Gain:</strong> ₹{result.finalGain}</p>
                      <p><strong>Tax Rate:</strong> {result.taxRate > 0 ? `${result.taxRate}%` : 'Taxable at slab rate'}</p>
                      <p><strong>Estimated Tax:</strong> ₹{result.taxAmount}</p>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CapitalGainsCalculator;